import { SpaceMember } from "@highnote/server/src/core/entities";
import { highnote } from "@highnote/server/src/sdk";
import { Unsubscribe } from "firebase/firestore";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  reportMembershipAttach,
  reportMembershipError,
  reportMembershipSaturated,
} from "App/modules/membershipTelemetry";
import { useMembershipFlags } from "../MembershipFlagsContext";

/**
 * The hard cap on the global my-memberships stream. We sized it so that a
 * cold boot costs the same order of magnitude as today's sidebar and Library
 * queries (100 + 150 full space docs); a member doc is about 300 bytes.
 *
 * When a user belongs to more spaces than this, the stream is saturated: the
 * window is full, so it can no longer show every membership. Two things
 * change:
 *
 *  - Absence from the map is no longer proof of "no member doc". The
 *    permission engine falls back to a point read (`ensureMember`) and, until
 *    that read lands, to the legacy in-document role map.
 *  - The provider suppresses cooperative revocation teardown, because
 *    Firestore reports a doc that leaves a limited window as "removed", and
 *    we cannot tell that apart from a real revocation. Enforced teardown is
 *    unaffected.
 *
 * Nobody ever measured the production distribution of memberships per user.
 * 500 is a guess, and `membership.stream_saturated` is its only instrument.
 */
export const MY_MEMBERS_LIMIT = 500;

/** Matches today's sidebar: `limit(100)`, and `loadMore` grows it in steps
 *  of 100. */
export const MY_ROOTS_PAGE_SIZE = 100;

/**
 * `undefined` means unknown: the stream is not loaded yet, or it is saturated
 * and the point read did not land yet, or no stream covers this identity at
 * all. Callers must fall back to the legacy in-document role map. They must
 * never read `undefined` as "no access".
 *
 * `null` means confirmed absent: the identity has no member doc on that
 * space.
 *
 * `SpaceMember` is the doc itself.
 */
export type ResolvedMember = SpaceMember | null | undefined;

type StreamState = { loading: boolean; saturated: boolean };

type MyMembershipsContextValue = {
  resolveMember: (spaceId: Id, memberId: Id) => ResolvedMember;
  /** The sidebar roots, in `spaceName asc` order. The server orders them. */
  roots: SpaceMember[];
  rootsLoading: boolean;
  rootsTotalLimit: number;
  loadMoreRoots: () => void;
  /** Every member doc for my uid, keyed by spaceId. */
  membersBySpaceId: Map<Id, SpaceMember>;
  loading: boolean;
  /** The spaceIds whose member doc disappeared for one of my identities in
   *  this session. */
  revokedSpaceIds: Set<Id>;
  acknowledgeRevoked: (spaceId: Id) => void;
};

const MyMembershipsContext = createContext<MyMembershipsContextValue>({
  resolveMember: () => undefined,
  roots: [],
  rootsLoading: true,
  rootsTotalLimit: 0,
  loadMoreRoots: () => undefined,
  membersBySpaceId: new Map(),
  loading: true,
  revokedSpaceIds: new Set(),
  acknowledgeRevoked: () => undefined,
});

/**
 * A space id is a UUID, and a member id can contain `|`, `:`, `+`, `_`, `.`
 * and `@`, so the separator must be a character that neither half can hold.
 *
 * `/` is that character, and the platform proves it: both halves are
 * Firestore document ids, and Firestore rejects `/` in a document id, because
 * `/` is its path separator. So the key splits only one way, by the same rule
 * that makes the underlying document path split only one way. And the key
 * reads like the path it stands for.
 */
const CACHE_KEY_SEPARATOR = "/";

const cacheKey = (spaceId: Id, memberId: Id) =>
  `${spaceId}${CACHE_KEY_SEPARATOR}${memberId}`;

/**
 * The suffix that every cache key for one identity ends with. The share-key
 * rotation cleanup has the identity but not the spaces, so it matches on this
 * suffix.
 */
const cacheKeySuffix = (memberId: Id) => `${CACHE_KEY_SEPARATOR}${memberId}`;

export const MyMembershipsContextProvider = ({
  authId,
  shareKey,
  children,
}: {
  authId?: Id;
  shareKey?: string;
  children: React.ReactNode;
}) => {
  /* The two-step shape is deliberate, and it outlives the six flags it used
   * to reconcile: this flag opens the collection-group streams, and the
   * consumers below read them. This used to be `isAnyEnabled`, a six-way
   * "or" over the client flags. The streams had to be open before any
   * individual consumer flag can take effect. There is one server-side mode
   * now, so the "or" is exact rather than approximate, and the shape stays.
   */
  const { membersV2Enabled } = useMembershipFlags();

  const [members, setMembers] = useState<Map<string, SpaceMember>>(new Map());
  const [absent, setAbsent] = useState<Set<string>>(new Set());
  const [streams, setStreams] = useState<Map<Id, StreamState>>(new Map());
  const [roots, setRoots] = useState<SpaceMember[]>([]);
  const [rootsLoading, setRootsLoading] = useState<boolean>(true);
  const [rootsTotalLimit, setRootsTotalLimit] =
    useState<number>(MY_ROOTS_PAGE_SIZE);
  const [revokedSpaceIds, setRevokedSpaceIds] = useState<Set<Id>>(new Set());

  const inFlightRef = useRef<Set<string>>(new Set());
  const unmountedRef = useRef<boolean>(false);

  useEffect(
    () => () => {
      unmountedRef.current = true;
    },
    [],
  );

  /**
   * Replaces every cached doc for one identity with the snapshot, so a doc
   * that disappeared on the server also disappears here. The same pass drops
   * the point-read results for that identity, because the stream is now the
   * newer truth.
   */
  const applySnapshot = useCallback(
    (
      memberId: Id,
      {
        members: snapshotMembers,
        removedSpaceIds,
        isSaturated,
      }: {
        members: SpaceMember[];
        removedSpaceIds: Id[];
        isSaturated: boolean;
      },
    ) => {
      if (unmountedRef.current) return;

      setMembers((prev) => {
        const next = new Map(prev);
        prev.forEach((member, key) => {
          if (member.memberId === memberId) next.delete(key);
        });
        snapshotMembers.forEach((member) => {
          next.set(cacheKey(member.spaceId, member.memberId), member);
        });
        return next;
      });

      setAbsent((prev) => {
        const next = new Set(prev);
        snapshotMembers.forEach((member) => {
          next.delete(cacheKey(member.spaceId, member.memberId));
        });
        return next;
      });

      setStreams((prev) => {
        const next = new Map(prev);
        next.set(memberId, { loading: false, saturated: isSaturated });
        return next;
      });

      /* The saturation instrument: once per session per identity. */
      if (isSaturated) reportMembershipSaturated(memberId);

      /* A saturated stream cannot tell a real revocation from a doc that
       * only fell out of the limited window. */
      if (isSaturated || removedSpaceIds.length < 1) return;

      setRevokedSpaceIds((prev) => {
        const next = new Set(prev);
        removedSpaceIds.forEach((spaceId) => next.add(spaceId));
        return next;
      });
    },
    [],
  );

  /* Every member doc for my uid.
   *
   * There is deliberately no equivalent stream for the token's share-key
   * identity. A collection-group query keyed on an attacker-supplied share
   * claim is a reverse lookup on an arbitrary string. Against a
   * password-protected space, the returned document confirms "this key opens
   * space X" without the password. The password gate lives on the space
   * read, not on the member document. The rules deny the query. And a
   * permanently denied listener sits in the error stream at a constant
   * non-zero rate, which is exactly the noise floor that hides a real
   * regression. `resolveMember` point-reads the share identity instead; see
   * below. */
  useEffect(() => {
    if (!membersV2Enabled || !authId) return;

    setStreams((prev) => {
      const next = new Map(prev);
      next.set(authId, { loading: true, saturated: false });
      return next;
    });

    reportMembershipAttach("my-members");
    const unsubscribe: Unsubscribe = highnote.watchMyMembers({
      memberId: authId,
      limit: MY_MEMBERS_LIMIT,
      onChange: (props) => applySnapshot(authId, props),
      onError: (error) => {
        /* A collection-group `list` fails as a whole, with no partial
         * results. We leave `loading: true`, so `resolveMember` keeps its
         * answer at `undefined` and every consumer stays on the legacy
         * fields. That is the correct failure mode for as long as the server
         * still writes the legacy fields. */
        console.log("Could not watch my memberships:", error);
        reportMembershipError("my-members", error);
      },
    });

    return () => {
      unsubscribe();
    };
  }, [membersV2Enabled, authId, applySnapshot]);

  /* A token refresh can drop or rotate the share claim. Forget its cached
   * member documents, so a stale grant cannot outlive the token that carried
   * it. This deliberately does not feed `revokedSpaceIds`: a lost claim is a
   * token refresh, not a revocation, and to treat it as one navigates a user
   * off a space they can still read through their uid. */
  const previousShareKeyRef = useRef<string>();
  useEffect(() => {
    const previous = previousShareKeyRef.current;
    previousShareKeyRef.current = shareKey;
    if (!previous || previous === shareKey) return;

    const suffix = cacheKeySuffix(previous);

    setMembers((prev) => {
      const next = new Map(prev);
      prev.forEach((member, key) => {
        if (member.memberId === previous) next.delete(key);
      });
      return next;
    });
    setAbsent((prev) => {
      const next = new Set(prev);
      prev.forEach((key) => {
        if (key.endsWith(suffix)) next.delete(key);
      });
      return next;
    });
  }, [shareKey]);

  /* The sidebar roots. The server orders them. */
  useEffect(() => {
    if (!membersV2Enabled || !authId) {
      setRootsLoading(false);
      return;
    }

    setRootsLoading(true);
    reportMembershipAttach("my-roots");
    const unsubscribe = highnote.watchMyTopLevelMembers({
      memberId: authId,
      limit: rootsTotalLimit,
      onChange: (_roots) => {
        if (unmountedRef.current) return;
        setRoots(_roots);
        setRootsLoading(false);
      },
      onError: (error) => {
        if (unmountedRef.current) return;
        /* Do not clear `roots`: an empty sidebar is the single worst failure
         * mode of this migration. Keep the last good list and report. */
        setRootsLoading(false);
        console.log("Could not watch my top level memberships:", error);
        reportMembershipError("my-roots", error);
      },
    });

    return () => {
      unsubscribe();
    };
  }, [membersV2Enabled, authId, rootsTotalLimit]);

  const ensureMember = useCallback((spaceId: Id, memberId: Id) => {
    const key = cacheKey(spaceId, memberId);
    if (inFlightRef.current.has(key)) return;
    inFlightRef.current.add(key);

    highnote
      .getSpaceMember({ spaceId, memberId })
      .then((member) => {
        if (unmountedRef.current) return;
        if (member) {
          setMembers((prev) => new Map(prev).set(key, member));
          return;
        }
        setAbsent((prev) => new Set(prev).add(key));
      })
      .catch((e) => {
        /* Leave the pair unknown, so the caller stays on the legacy fields. */
        console.log(`Could not read member [${key}]:`, e);
      })
      .finally(() => {
        inFlightRef.current.delete(key);
      });
  }, []);

  const resolveMember = useCallback(
    (spaceId: Id, memberId: Id): ResolvedMember => {
      if (!spaceId || !memberId) return undefined;

      const key = cacheKey(spaceId, memberId);
      const member = members.get(key);
      if (member) return member;
      if (absent.has(key)) return null;

      const stream = streams.get(memberId);

      if (!stream) {
        /* `authId` does get a stream: an effect registers it, and effects
         * run after the first render. So "no stream yet" for the uid means
         * the stream still loads, not "no stream will ever come", and the
         * right answer is to wait.
         *
         * A point read here instead costs one wasted `get`, on every cold
         * load, for each space asked about during the first paint. The
         * stream then answers them all right after. */
        if (memberId === authId) return undefined;

        /* Any other identity is one that no stream covers. In practice that
         * is the share-key case, because the rules deny the collection-group
         * query for a share claim. A per-space `get` is the only lawful
         * answer. It costs 0 rule document accesses (the rule compares
         * `memberId` on the document under evaluation). And it cannot
         * enumerate, that is, it cannot list every space a key opens. */
        ensureMember(spaceId, memberId);
        return undefined;
      }

      if (stream.loading) return undefined;

      /* A loaded, unsaturated stream is the complete set of this identity's
       * memberships, so absence from it is a confirmed "no member doc", and
       * that answer is free. */
      if (!stream.saturated) return null;

      ensureMember(spaceId, memberId);
      return undefined;
    },
    [members, absent, streams, ensureMember, authId],
  );

  const acknowledgeRevoked = useCallback((spaceId: Id) => {
    setRevokedSpaceIds((prev) => {
      if (!prev.has(spaceId)) return prev;
      const next = new Set(prev);
      next.delete(spaceId);
      return next;
    });
  }, []);

  const membersBySpaceId = useMemo(() => {
    const bySpaceId = new Map<Id, SpaceMember>();
    if (!authId) return bySpaceId;
    members.forEach((member) => {
      if (member.memberId === authId) bySpaceId.set(member.spaceId, member);
    });
    return bySpaceId;
  }, [members, authId]);

  /* A stream that is not registered yet counts as `loading`, not loaded. A
   * mistake here makes the engine report a `resolver_miss` for every space
   * on every boot, and that noise hides the one signal that gates the
   * deletion of the legacy fields. */
  const authStream = authId ? streams.get(authId) : undefined;
  const loading =
    membersV2Enabled && (!authId || !authStream || authStream.loading);

  const value = useMemo(
    () => ({
      resolveMember,
      roots,
      rootsLoading,
      rootsTotalLimit,
      loadMoreRoots: () =>
        setRootsTotalLimit((prev) => prev + MY_ROOTS_PAGE_SIZE),
      membersBySpaceId,
      loading,
      revokedSpaceIds,
      acknowledgeRevoked,
    }),
    [
      resolveMember,
      roots,
      rootsLoading,
      rootsTotalLimit,
      membersBySpaceId,
      loading,
      revokedSpaceIds,
      acknowledgeRevoked,
    ],
  );

  return (
    <MyMembershipsContext.Provider value={value}>
      {children}
    </MyMembershipsContext.Provider>
  );
};

export const useMyMemberships = () => useContext(MyMembershipsContext);
